import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

/**
 * A common service to facilitate adding structured data to a site.
 * @see https://developers.google.com/search/docs/advanced/structured-data/dataset
 */
@Injectable({ providedIn: 'root' })
export class JsonLdService {
  static SCRIPT_TAG_ID = 'STRUCTURED_DATA';

  /** the DOM Document */
  private _document: Document;

  constructor(
    // see https://github.com/angular/angular/issues/20351 and https://angular.io/guide/angular-compiler-options#strictmetadataemit
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Inject(DOCUMENT) _document: any
  ) {
    this._document = _document as Document;
  }

  /**
   * Remove the existing structured data element from the DOM if it exists.
   */
  remove(): void {
    const structuredDataElement = this._document.getElementById(JsonLdService.SCRIPT_TAG_ID);
    if (structuredDataElement) {
      if (structuredDataElement.parentNode) {
        structuredDataElement.parentNode.removeChild(structuredDataElement);
      } else {
        structuredDataElement.remove();
      }
    }
  }

  /**
   * Insert a new structured data script tag in the head of the DOM. This will remove and existing
   * structured data element if it exists.
   *
   * @param schema The contents of the script tag, the structure data schema to insert.
   */
  insert(schema: Record<string, unknown>): void {
    let structuredDataElement = this._document.getElementById(JsonLdService.SCRIPT_TAG_ID);

    // If the element already exists, remove it.
    if (structuredDataElement) {
      this.remove();
    }

    structuredDataElement = this._document.createElement('script');
    structuredDataElement.setAttribute('id', JsonLdService.SCRIPT_TAG_ID);
    structuredDataElement.setAttribute('type', 'application/ld+json');
    structuredDataElement.innerText = JSON.stringify(schema);
    this._document.head.appendChild(structuredDataElement);
  }
}
